import { supabase } from '../../supabaseClient';
import { ENTRY_TYPE, INSTALLMENT_STATUS } from '../constants/financial';
import { todayISO } from './financialPeriod';
import { fetchAllRows } from './supabaseFetchAll';
import { markInstallmentAsPaid, saveInstallments } from './installment';
import { saveBreakdownRows } from './breakdown';

export const saveFinancialEntry = async (id = undefined, item, userId = undefined) => {
  if (id) {
    // RN05: rastreabilidade de edição (quem/quando) no update do lançamento.
    return await supabase
      .from('FinancialEntry')
      .update({ ...item, updated_at: new Date(), updated_by: userId ?? null })
      .match({ id });
  }
  return await supabase
    .from('FinancialEntry')
    .insert([{ ...item }]);
};

export const loadFinancialEntries = async (companyId) => {
  let { data, error } = await supabase
    .from('FinancialEntry')
    .select('*, AccountPlan(*), CostCenter(*)')
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (!error) {
    return data;
  }
  return [];
};

export const loadFinancialEntryById = async (id) => {
  let { data, error } = await supabase
    .from('FinancialEntry')
    .select('*, AccountPlan(*), CostCenter(*)')
    .eq('id', id)
    .is('deleted_at', null);
  if (!error && data?.length > 0) {
    return data[0];
  }
  return null;
};

export const deleteFinancialEntry = async (id, userId) => {
  return await supabase
    .from('FinancialEntry')
    .update({ deleted_at: new Date(), deleted_by: userId })
    .match({ id });
};

// Meses distintos ('YYYY-MM') com lançamentos da empresa, ordenados.
export const loadEntryMonths = async (companyId) => {
  if (!companyId) return [];
  const { data, error } = await fetchAllRows(() =>
    supabase
      .from('FinancialEntry')
      .select('issue_date')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .order('id', { ascending: true })
  );
  if (error || !data) return [];

  const months = new Set();
  data.forEach((e) => {
    if (e.issue_date) months.add(e.issue_date.slice(0, 7));
  });
  return Array.from(months).sort();
};

// Lista lançamentos (FinancialEntry) de uma empresa dentro de um range de
// issue_date, opcionalmente filtrando por type. Inclui AccountPlan, CostCenter
// e as Installments (não-deletadas) para alimentar a tela de listagem com
// edição/exclusão — a tela precisa saber se há parcela paga para aplicar a
// regra B (lock de campos que afetam saldo + bloqueio de delete).
export const loadFinancialEntriesByPeriodAndType = async (companyId, range, type) => {
  if (!companyId) return [];

  const buildQuery = () => {
    let query = supabase
      .from('FinancialEntry')
      .select('*, AccountPlan(*), CostCenter(*), Installment(id, status, value, due_date, payment_date, deleted_at)')
      .eq('company_id', companyId)
      .is('deleted_at', null);

    // Bounds opcionais: filtro "Todos" passa range nulo e lista o histórico completo.
    if (range?.from) {
      query = query.gte('issue_date', range.from);
    }
    if (range?.to) {
      query = query.lte('issue_date', range.to);
    }
    if (type) {
      query = query.eq('type', type);
    }

    return query
      .order('issue_date', { ascending: false })
      .order('id', { ascending: true });
  };

  const { data, error } = await fetchAllRows(buildQuery);
  if (error || !data) return [];

  // Filtra installments soft-deletadas (o join não suporta `.is` em tabela aninhada
  // sem !inner, que perderia entries sem parcelas — então filtramos aqui).
  return data.map((entry) => ({
    ...entry,
    Installment: (entry.Installment || []).filter((i) => !i.deleted_at),
  }));
};

// Busca os FinancialEntry vinculados a uma Ordem de Serviço. No modelo novo a
// cobrança é UM único lançamento (com breakdown por categoria à parte, para a
// DRE); lançamentos legados por categoria (1.1/1.2/1.3) ainda retornam vários.
// Usado na idempotência do botão "Cobrar Cliente" e no bloqueio de exclusão da OS.
export const loadFinancialEntriesByOS = async (orderServiceId) => {
  if (!orderServiceId) return [];
  const { data, error } = await supabase
    .from('FinancialEntry')
    .select('*, AccountPlan(*)')
    .eq('order_service_id', orderServiceId)
    .is('deleted_at', null)
    .order('account_plan_id', { ascending: true });
  if (error || !data) return [];
  return data;
};

// Gera a cobrança de uma OS como UM único lançamento (a verdade de caixa): as
// parcelas são reais (o cronograma informado, sem rateio por parcela) e a
// separação por serviço vai para o breakdown (FinancialEntryBreakdown), lido só
// pela DRE. O account_plan_id do lançamento é o da categoria de maior valor —
// serve apenas para os cards/gráfico somarem como receita; a DRE usa o breakdown.
//
// Parâmetros:
//  - shared: { company_id, order_service_id, document, supplier_client,
//             description, issue_date, created_by }
//  - categories: [{ key, code, account_plan_id, value }]  (apenas value > 0)
//  - installments: [{ installment_number, due_date, value, payment_method,
//                     bank_id, status }]  (cronograma real, valores numéricos)
//
// Retorna { error, entryIds }. Em caso de erro parcial, retorna o primeiro erro
// (mesma semântica não-atômica já aceita no projeto — sem RPC).
export const saveOSCharge = async ({ shared, categories, installments }) => {
  const validCategories = categories.filter((c) => (Number(c.value) || 0) > 0);
  const grandTotal = installments.reduce(
    (s, inst) => s + (Number(inst.value) || 0),
    0
  );

  // Categoria de maior valor → account_plan_id do lançamento único (receita).
  const primary = validCategories.reduce(
    (max, c) => ((Number(c.value) || 0) > (Number(max?.value) || 0) ? c : max),
    validCategories[0] || null
  );

  const today = todayISO();
  const resolvePaymentDate = (dueDate) =>
    dueDate && dueDate <= today ? dueDate : today;

  const { data: entryData, error: entryError } = await saveFinancialEntry(
    undefined,
    {
      company_id: shared.company_id,
      type: ENTRY_TYPE.RECEITA,
      document: shared.document || null,
      issue_date: shared.issue_date || null,
      supplier_client: (shared.supplier_client || '').toUpperCase(),
      account_plan_id: primary?.account_plan_id || null,
      total_value: grandTotal,
      description: shared.description || null,
      order_service_id: shared.order_service_id || null,
      created_by: shared.created_by,
    }
  );
  if (entryError) return { error: entryError, entryIds: [] };

  const entryId = entryData?.[0]?.id;
  if (!entryId) {
    return { error: new Error('Erro ao obter ID do lançamento.'), entryIds: [] };
  }
  const entryIds = [entryId];

  // Parcelas reais — SEMPRE PENDENTE; as marcadas como PAGO no cronograma são
  // promovidas via markInstallmentAsPaid (move Bank.balance).
  const payloads = installments.map((inst) => ({
    financial_entry_id: entryId,
    installment_number: inst.installment_number,
    due_date: inst.due_date,
    value: inst.value,
    payment_method: inst.payment_method || null,
    bank_id: inst.bank_id || null,
    status: INSTALLMENT_STATUS.PENDENTE,
    created_by: shared.created_by,
  }));
  const { data: insertedRows, error: instError } = await saveInstallments(payloads);
  if (instError) return { error: instError, entryIds };

  // Breakdown por categoria (para a DRE) — os líquidos, somando o total.
  const { error: bdError } = await saveBreakdownRows(
    entryId,
    validCategories.map((c) => ({
      account_plan_id: c.account_plan_id,
      value: Number(c.value) || 0,
    })),
    shared.created_by
  );
  if (bdError) return { error: bdError, entryIds };

  for (let i = 0; i < installments.length; i++) {
    if (installments[i].status !== INSTALLMENT_STATUS.PAGO) continue;
    const id = insertedRows?.[i]?.id;
    if (!id) continue;
    const { error: payErr } = await markInstallmentAsPaid(id, {
      payment_date: resolvePaymentDate(installments[i].due_date),
      payment_method: installments[i].payment_method,
      bank_id: installments[i].bank_id,
    });
    if (payErr) return { error: payErr, entryIds };
  }

  return { error: null, entryIds };
};
